import styled from 'styled-components';

/**
 * A two-column "side-by-side" content row: text on one side, media (image/video)
 * on the other. On mobile both variants stack vertically with the media on top,
 * separated by a 30px gap.
 *
 * Children provide the row wrapper and columns via class names:
 *
 * - `image-left` — media column first in the DOM (desktop: media left, text right).
 *   Media column uses `left-img`, text column uses `right-text`.
 * - `image-right` — text column first in the DOM (desktop: text left, media right).
 *   Text column uses `left-text`, media column uses `right`.
 *
 * Note: these intent-based variant names replace the older page-local
 * `container` / `reverse-container` classes, which meant opposite mobile
 * stacking orders on different pages. Here, `image-left` stacks `column` and
 * `image-right` stacks `column-reverse`, so the media always ends up on top
 * on mobile.
 *
 * Add `text-first-mobile` alongside the variant class to flip the mobile
 * stacking order so the text ends up on top instead (desktop unaffected).
 *
 * ```tsx
 * <SideBySideSection>
 *   <div className="image-left">
 *     <div className="left-img">{media}</div>
 *     <div className="right-text">{text}</div>
 *   </div>
 * </SideBySideSection>
 * ```
 */
export const SideBySideSection = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  width: 100%;
  .image-left,
  .image-right {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    max-width: 1160px;
    width: 100%;
    gap: 60px;
  }
  .left-img,
  .right {
    flex: 1;
    max-width: 566px;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .left-img {
    margin-top: 35px;
  }
  .left-text {
    max-width: 498px;
  }
  .right-text {
    max-width: 468px;
  }
  .right-text,
  .left-text {
    flex: 1;
  }
  h1 {
    padding-bottom: 32px;
    margin-bottom: 0;
  }
  @media (max-width: 768px) {
    .image-left {
      flex-direction: column;
      align-items: flex-start;
    }
    .image-right {
      flex-direction: column-reverse;
      align-items: flex-start;
    }
    .image-left.text-first-mobile {
      flex-direction: column-reverse;
    }
    .image-right.text-first-mobile {
      flex-direction: column;
    }
    .image-left,
    .image-right {
      gap: 30px;
    }
    .right-text,
    .left-text {
      max-width: none;
    }
    .left-img {
      margin-top: 12px;
    }
    .left-img,
    .right {
      max-width: none;
      width: 100%;
      .video {
        border-radius: 20px !important;
      }
    }
  }
`;
